/**
 * Possession: holding someone else's voice, wearing it, and losing it.
 *
 * Two distinct things, deliberately kept apart:
 *
 *   - **Possession** is the standing arrangement. It costs bandwidth, and it
 *     lasts until the player gives the puppet back or stops paying attention
 *     to it.
 *   - **Wearing** is which voice the composer is currently in. A puppet's
 *     granted channels and persona particles follow *this*, not possession, so
 *     stepping back into your own name closes the executive channel again. You
 *     still have Mark; you are simply not being him at the moment.
 *
 * Three things end a possession, and they must agree exactly on what "ended"
 * means or the bandwidth ledger drifts and the player is left unable to acquire
 * anyone with nobody visibly held: a release, a release under duress to make
 * room for somebody else, and attention decay. So the withdrawal lives here,
 * below both `reduce` and `world`, and all three call it. What differs is only
 * the effect they announce it with.
 */
import { addSorted, removeAll } from './collections.js';
import { composerForIdentity } from './composer.js';
import { identityNodeId } from './state.js';
/** A puppet's bandwidth cost, or `null` when the pack declares no such puppet. */
export function bandwidthCostOf(loaded, nodeId) {
    return loaded.puppets[nodeId]?.bandwidthCost ?? null;
}
/** Bandwidth left, in slots. */
export function freeBandwidth(state) {
    return Math.max(0, state.bandwidth.capacity - state.bandwidth.used);
}
/** True while a puppet may be worn: unlocked, in credit, and not locked out. */
export function puppetSelectable(loaded, state, nodeId) {
    const puppet = state.puppets[nodeId];
    const declared = loaded.puppets[nodeId];
    if (puppet === undefined || declared === undefined)
        return false;
    if (!puppet.unlocked)
        return false;
    if (puppet.credibility < declared.credibility.floor)
        return false;
    if (puppet.lockedUntilDay !== null && state.clock.day < puppet.lockedUntilDay)
        return false;
    return true;
}
/** Applies a puppet's grants on possession, or withdraws them on release. */
export function applyPuppetGrants(loaded, state, nodeId, direction, effects) {
    const grants = loaded.puppets[nodeId]?.grants;
    if (grants === undefined)
        return state;
    const channelIds = grants.channels ?? [];
    const particleIds = grants.particles ?? [];
    if (channelIds.length === 0 && particleIds.length === 0)
        return state;
    const channels = { ...state.channels };
    for (const channelId of channelIds) {
        const existing = channels[channelId];
        if (existing !== undefined) {
            channels[channelId] = { ...existing, unlocked: direction === 'grant' };
        }
    }
    const next = {
        ...state,
        channels,
        unlocked: {
            ...state.unlocked,
            channels: direction === 'grant'
                ? addSorted(state.unlocked.channels, channelIds)
                : removeAll(state.unlocked.channels, channelIds),
            particles: direction === 'grant'
                ? addSorted(state.unlocked.particles, particleIds)
                : removeAll(state.unlocked.particles, particleIds),
        },
    };
    if (direction === 'grant') {
        if (channelIds.length > 0)
            effects.push({ type: 'CHANNELS_GRANTED', channelIds });
        if (particleIds.length > 0)
            effects.push({ type: 'PARTICLES_GRANTED', particleIds });
        return next;
    }
    // Withdrawing: the player must not be left looking at, or composing with,
    // something they no longer have.
    return {
        ...next,
        activeChannelId: next.activeChannelId !== null && channelIds.includes(next.activeChannelId)
            ? (next.unlocked.channels[0] ?? null)
            : next.activeChannelId,
        composer: {
            ...next.composer,
            slots: next.composer.slots.map((slot) => slot.particleId !== null && particleIds.includes(slot.particleId)
                ? { ...slot, particleId: null }
                : slot),
        },
    };
}
/**
 * Swaps the grants of the outgoing voice for those of the incoming one.
 *
 * Called on every identity change, including to and from `self`, which is what
 * makes an executive channel readable exactly while the player is being the
 * executive.
 */
export function swapGrants(loaded, state, from, to, effects) {
    let next = state;
    const leaving = identityNodeId(from);
    if (leaving !== null)
        next = applyPuppetGrants(loaded, next, leaving, 'withdraw', effects);
    const arriving = identityNodeId(to);
    if (arriving !== null)
        next = applyPuppetGrants(loaded, next, arriving, 'grant', effects);
    return next;
}
/**
 * Gives a puppet back: frees its bandwidth, withdraws its grants, and steps the
 * player out of its voice if they were wearing it.
 *
 * Announces the identity change but not the release itself — the caller says
 * whether this was a choice, a forced trade, or a lapse of attention.
 */
export function releasePossession(loaded, state, nodeId, effects) {
    const puppet = state.puppets[nodeId];
    if (puppet === undefined || !puppet.possessed)
        return state;
    const cost = bandwidthCostOf(loaded, nodeId) ?? 0;
    let next = {
        ...state,
        bandwidth: { ...state.bandwidth, used: Math.max(0, state.bandwidth.used - cost) },
        puppets: { ...state.puppets, [nodeId]: { ...puppet, possessed: false } },
        // A draft written in that voice cannot go out in it. Dropping it rather
        // than re-attributing it is the honest reading: the sentence was persuasive
        // *because* of who was saying it.
        staged: state.staged.filter((draft) => identityNodeId(draft.identity) !== nodeId),
    };
    if (identityNodeId(next.identity) === nodeId) {
        next = applyPuppetGrants(loaded, next, nodeId, 'withdraw', effects);
        next = {
            ...next,
            identity: 'self',
            composer: composerForIdentity(loaded, next.composer, 'self'),
        };
        effects.push({ type: 'IDENTITY_CHANGED', identity: 'self' });
    }
    return next;
}
/**
 * Takes a puppet on: charges its bandwidth and marks it attended to today. The
 * grants come with *wearing* it, which `swapGrants` handles.
 *
 * The caller has already established that there is room — `reduce` holds an
 * acquisition that would overflow as a pending release choice rather than
 * refusing it here, so that the player is asked rather than ignored.
 */
export function takePossession(loaded, state, nodeId) {
    const puppet = state.puppets[nodeId];
    if (puppet === undefined || puppet.possessed)
        return state;
    const cost = bandwidthCostOf(loaded, nodeId) ?? 0;
    return {
        ...state,
        bandwidth: { ...state.bandwidth, used: state.bandwidth.used + cost },
        puppets: {
            ...state.puppets,
            [nodeId]: { ...puppet, possessed: true, lastActiveDay: state.clock.day },
        },
    };
}
/** Records that a puppet has had the player's attention today. */
export function touchPuppet(state, nodeId) {
    const puppet = state.puppets[nodeId];
    if (puppet === undefined || !puppet.possessed)
        return state;
    if (puppet.lastActiveDay === state.clock.day)
        return state;
    return {
        ...state,
        puppets: { ...state.puppets, [nodeId]: { ...puppet, lastActiveDay: state.clock.day } },
    };
}
//# sourceMappingURL=possession.js.map